import React, { useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const LiftingForTwoPage = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setErrorMessage('Please enter a valid email address.');
            setStatus('error');
            return;
        }

        setStatus('loading');
        try {
            await addDoc(collection(db, 'lifting-for-two-waitlist'), {
                email,
                source: 'landing-page',
                createdAt: serverTimestamp(),
            });
            setStatus('success');
            setEmail('');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error adding to waitlist:', err);
            setErrorMessage('Something went wrong. Please try again.');
            setStatus('error');
        }
    };

    return (
        <HelmetProvider>
            <div className="flex-1 bg-cream text-ink">
                <Helmet prioritizeSeoTags>
                    <title>Lifting for Two | Pregnancy Strength Training for Experienced Lifters</title>
                    <link rel="canonical" href="https://amandafilomena.com/programs/lifting-for-two" />
                    <meta name="description" content="A pregnancy lifting program for women who already train. Don't start over. Adapt, adjust, and keep building. Treat pregnancy like a bulk." />
                    <meta property="og:title" content="Lifting for Two | Don't Start Over. Keep Building." />
                    <meta property="og:description" content="Pregnancy strength training for experienced lifters. Adapt your training, keep your intensity, build for labor and recovery." />
                </Helmet>

                {/* Hero Section */}
                <section className="bg-ink text-cream px-6 py-20 md:py-32">
                    <div className="mx-auto max-w-4xl text-center">
                        <p className="text-xs font-medium tracking-[0.25em] text-terra uppercase mb-6">
                            Coming Soon
                        </p>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl leading-tight mb-8" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                            Lifting for Two
                        </h1>
                        <p className="text-xl md:text-2xl text-cream/80 mb-6 max-w-2xl mx-auto leading-relaxed">
                            You didn't stop being strong when you got pregnant.
                        </p>
                        <p className="text-lg text-cream/60 max-w-xl mx-auto">
                            A pregnancy training program for women who already lift — and have no intention of starting over.
                        </p>
                    </div>
                </section>

                {/* Problem Section */}
                <section className="px-6 py-20 md:py-28 border-b border-sand">
                    <div className="mx-auto max-w-3xl">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-6">The Problem</p>
                        <h2 className="text-2xl md:text-3xl mb-8" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                            Every pregnancy fitness guide assumes you're starting from zero.
                        </h2>
                        <div className="space-y-6 text-muted leading-relaxed">
                            <p>
                                You've spent years building strength. You know your body. You've dialed in your training.
                            </p>
                            <p>
                                Then you get pregnant — and suddenly every resource tells you to "take it easy," switch to walking, maybe try some prenatal yoga.
                            </p>
                            <p className="text-ink font-medium">
                                That's not you.
                            </p>
                            <p>
                                You don't need a beginner's guide. You need intelligent adjustments. You want to know what changes, what stays, and how to keep building — not start over.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Solution Section */}
                <section className="px-6 py-20 md:py-28 bg-surface border-b border-sand">
                    <div className="mx-auto max-w-3xl">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-6">The Approach</p>
                        <h2 className="text-2xl md:text-3xl mb-8" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                            Treat pregnancy like a bulk.
                        </h2>
                        <div className="space-y-6 text-muted leading-relaxed">
                            <p>
                                Your body is building something. Feed it. Train it. Respect the process — but don't abandon your identity as someone who lifts.
                            </p>
                            <p>
                                <strong className="text-ink">Lifting for Two</strong> is a framework for experienced lifters navigating pregnancy. Not a cookie-cutter program. A system of adjustments that respects what you've already built.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="px-6 py-20 md:py-28 border-b border-sand">
                    <div className="mx-auto max-w-4xl">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-6 text-center">What You'll Learn</p>
                        <h2 className="text-2xl md:text-3xl mb-12 text-center" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                            Keep building. Adapt intelligently.
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-surface border border-sand p-8">
                                <h3 className="text-lg mb-3 text-ink" style={{ fontFamily: "'Cinzel', serif" }}>Trimester-by-Trimester Adjustments</h3>
                                <p className="text-sm text-muted leading-relaxed">
                                    What to modify, what to keep, and when. Movement patterns, intensity guidelines, and recovery protocols that evolve with your pregnancy.
                                </p>
                            </div>
                            <div className="bg-surface border border-sand p-8">
                                <h3 className="text-lg mb-3 text-ink" style={{ fontFamily: "'Cinzel', serif" }}>Lift Heavy, Lift Smart</h3>
                                <p className="text-sm text-muted leading-relaxed">
                                    How to maintain intensity without risking injury. Breathing, bracing, and positioning adjustments that let you keep lifting with confidence.
                                </p>
                            </div>
                            <div className="bg-surface border border-sand p-8">
                                <h3 className="text-lg mb-3 text-ink" style={{ fontFamily: "'Cinzel', serif" }}>Build for Labor</h3>
                                <p className="text-sm text-muted leading-relaxed">
                                    Strength training that directly prepares you for delivery. Endurance, core stability, and positions that translate to the work of labor.
                                </p>
                            </div>
                            <div className="bg-surface border border-sand p-8">
                                <h3 className="text-lg mb-3 text-ink" style={{ fontFamily: "'Cinzel', serif" }}>Mental Game</h3>
                                <p className="text-sm text-muted leading-relaxed">
                                    The identity shift is real. Stay connected to who you are. Training isn't just physical — it's how you keep showing up as yourself.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* For You Section */}
                <section className="px-6 py-20 md:py-28 bg-surface border-b border-sand">
                    <div className="mx-auto max-w-3xl">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-6">This Is For You If</p>
                        <ul className="space-y-4">
                            {[
                                "You've been lifting consistently for at least a year before pregnancy",
                                "You want to keep training — not just \"stay active\"",
                                "You're frustrated by beginner-level pregnancy fitness advice",
                                "You see pregnancy as a season to adapt, not abandon your training",
                                "You want to be stronger for labor, recovery, and motherhood",
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-4">
                                    <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-terra" />
                                    <span className="text-muted">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* CTA / Email Capture Section */}
                <section className="px-6 py-20 md:py-28 bg-ink text-cream">
                    <div className="mx-auto max-w-2xl text-center">
                        <p className="text-xs font-medium tracking-[0.25em] text-terra uppercase mb-6">
                            Be First to Know
                        </p>
                        <h2 className="text-2xl md:text-3xl mb-6" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                            Join the Waitlist
                        </h2>
                        <p className="text-cream/70 mb-10 max-w-lg mx-auto">
                            Lifting for Two is in development. Join the waitlist to get early access, founding member pricing, and updates as we build.
                        </p>

                        {status === 'success' ? (
                            <div className="bg-terra/20 border border-terra/40 px-8 py-6">
                                <p className="text-lg text-cream font-medium">You're on the list.</p>
                                <p className="text-cream/70 text-sm mt-2">We'll be in touch when Lifting for Two is ready.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (status === 'error') setStatus('idle');
                                    }}
                                    placeholder="Enter your email"
                                    className="flex-1 px-5 py-4 bg-cream/10 border border-cream/20 text-cream placeholder:text-cream/40 focus:outline-none focus:border-terra transition-colors"
                                    disabled={status === 'loading'}
                                />
                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    className="px-8 py-4 bg-terra text-white text-sm font-medium uppercase tracking-wider hover:bg-terra/90 transition-colors disabled:opacity-50"
                                >
                                    {status === 'loading' ? 'Joining...' : 'Join Waitlist'}
                                </button>
                            </form>
                        )}

                        {status === 'error' && (
                            <p className="mt-4 text-red-400 text-sm">{errorMessage}</p>
                        )}

                        <p className="mt-6 text-cream/40 text-xs">
                            No spam. Just updates on the program launch.
                        </p>
                    </div>
                </section>

                {/* About Section */}
                <section className="px-6 py-20 md:py-28 border-b border-sand">
                    <div className="mx-auto max-w-3xl">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-6">About the Creator</p>
                        <h2 className="text-2xl md:text-3xl mb-8" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                            Built by someone who's done it.
                        </h2>
                        <div className="space-y-6 text-muted leading-relaxed">
                            <p>
                                I'm Amanda — AI engineer, founder, and mom of two. I lifted through both pregnancies, figuring out what worked through trial, error, and a lot of frustration with generic advice.
                            </p>
                            <p>
                                Lifting for Two is the program I wish existed. Built for women like me — ambitious, already training, and unwilling to treat pregnancy like a pause button.
                            </p>
                            <p>
                                <a href="/about" className="text-terra hover:underline">Learn more about me →</a>
                            </p>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="border-t border-sand px-6 py-8">
                    <div className="mx-auto max-w-6xl flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <p className="text-sm text-muted">© 2026 Amanda Filomena</p>
                        <div className="flex gap-6">
                            <a href="/" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                Home
                            </a>
                            <a href="/programs" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                Programs
                            </a>
                        </div>
                    </div>
                </footer>
            </div>
        </HelmetProvider>
    );
};

export default LiftingForTwoPage;
